'use client';

import { SentryMinimal } from '@bridebook/toolbox/src/sentry';
import { motifEditorCssHref } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/layout-motif/components/motif-css-link';
import { useSelector } from '@bridebook/web/lib/utils';
import { getMotifCurrentCssHash, getWeddingId } from '@bridebook/web/lib/weddings/selectors';
import { type QueryFunction, useQuery } from '@tanstack/react-query';

/** Stands in for the hash on the layout primitive, which is one fixed file with nothing to version. */
const PRIMITIVE_KEY = 'lp-billboard';

/** The cache key IS the content hash, so every surface fetching the same theme shares one entry. */
export const themeCssQueryKey = (cssHash: string | undefined) => [
  'motif-theme-css',
  cssHash ?? PRIMITIVE_KEY,
];

/** Fetches the theme stylesheet text from the immutable hash route (no hash → the primitive). */
export const themeCssQueryFn =
  (cssHash: string | undefined): QueryFunction<string> =>
  async ({ signal }) => {
    const response = await fetch(motifEditorCssHref(cssHash), { signal });
    if (!response.ok) throw new Error(`Theme CSS fetch failed: ${response.status}`);
    return response.text();
  };

/** A surface has a stylesheet, is still waiting for one, or has given up on one. */
export interface IThemeCssState {
  cssText: string | undefined;
  /** The default three retries are spent and no stylesheet is coming — the surface shows an error. */
  failed: boolean;
}

/**
 * Hashes already reported this page load. `onError` fires once per SUBSCRIBED SURFACE, and an errored
 * query is stale so every remount refetches and errors again, so one outage would otherwise fan out
 * across the panel, the tiles and every revisit.
 */
const reportedCssFailures = new Set<string>();

/**
 * One theme's stylesheet text by explicit hash, off the cache shared with every editor surface.
 * `keepPreviousData` keeps the old sheet while a new hash loads, and cached text is returned even while
 * `enabled` is false, so a lazily-mounted surface paints styled on its first frame.
 */
export const useThemeCssText = (
  cssHash: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
): IThemeCssState => {
  const { data, isError } = useQuery(themeCssQueryKey(cssHash), themeCssQueryFn(cssHash), {
    enabled,
    staleTime: Infinity,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    // The hash is reported too: one failing sheet among many is a bad stored pointer, all of them is the CDN.
    onError: (error) => {
      const key = cssHash ?? PRIMITIVE_KEY;
      if (reportedCssFailures.has(key)) return;
      reportedCssFailures.add(key);
      SentryMinimal().captureException(error, {
        tags: { source: 'useThemeCssText' },
        extra: { cssHash: key },
      });
    },
  });

  return { cssText: data, failed: isError && data == null };
};

/**
 * The one shared inline-CSS delivery for ALL editor surfaces (desktop panel, full-page modal, preview-mode
 * route, mobile drawer). Fetches the current draft theme via the bucket-backed hash route (immutable) and
 * hands the text back so surfaces render it as an inline <style> — no per-surface <link> round-trip.
 *
 * The query key IS the content hash, so an old version's cache entry is valid forever (`staleTime: Infinity`,
 * no `?v=` gymnastics — the hash is the version). `keepPreviousData` keeps the current stylesheet on screen
 * while a NEW hash loads, so a generation/undo/tap swap never flashes back to the primitive. No hash yet
 * (empty history) → the primitive route, cached under the `lp-billboard` key.
 */
export const useMotifThemeCss = (): IThemeCssState => {
  const cssHash = useSelector(getMotifCurrentCssHash);
  const weddingId = useSelector(getWeddingId);

  return useThemeCssText(cssHash, { enabled: Boolean(weddingId) });
};
